import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { Box, MeshReflectorMaterial } from '@react-three/drei';
import * as THREE from 'three';
import { DisplaySpot } from './DisplaySpot';
import { Model3D } from './Scene/components/Model3D';
import { useRoom } from './Scene/hooks/useRoom';
import { calculateAllSpotPositions, createHexagonalRoomGeometry } from './Scene/utils/roomUtils';
import { ROOM_TYPES } from './Scene/config/roomTypes';
import { useGalleryAssets } from '../../hooks/useGalleryAssets';
import CanvasButton from './Scene/components/CanvasButton';
import HexagonalRoom from './Scene/components/HexagonalRoom';
import GlbRoom from '../Gallery/Scene/components/GlbRoom';

const DEBUG = true;
const USE_STATIC_PODIUMS = true; // Feature flag for gradual transition

// Debug helper
const debugLog = (...args) => {
    if (DEBUG) console.log(...args);
};

// Helper to validate vector data
const isValidVector = (data) => {
    if (Array.isArray(data)) {
        return data.length === 3 && data.every(val => Number.isFinite(val));
    }
    if (data && typeof data === 'object') {
        return ['x', 'y', 'z'].every(key => Number.isFinite(data[key]));
    }
    return false;
};

// Add at the top with other helpers
const ensureNumber = (value, defaultValue = 0) => {
    return Number.isFinite(value) ? value : defaultValue;
};

// Helper to create safe dimensions
const createSafeDimensions = (dims) => {
    return {
        width: ensureNumber(dims?.width, 10),
        height: ensureNumber(dims?.height, 8),
        depth: ensureNumber(dims?.depth, 10),
        wallThickness: ensureNumber(dims?.wallThickness, 0.2),
        doorWidth: ensureNumber(dims?.doorWidth, 4)
    };
};

// Debug helper
const debugComponent = (name, props) => {
    console.log(`Rendering ${name}:`, props);
};

// Add this helper function at the top
const createTransform = (data, defaultValue) => {
    if (!data) return defaultValue;

    try {
        if (Array.isArray(data)) {
            return data;
        }
        if (data instanceof THREE.Vector3) {
            return [data.x, data.y, data.z];
        }
        if (data instanceof THREE.Euler) {
            return [data.x, data.y, data.z];
        }
        return defaultValue;
    } catch (error) {
        console.error('Transform creation error:', error);
        return defaultValue;
    }
};

// Static podium component - will replace interactive Podium
const StaticPodium = ({ position }) => {
    const baseHeight = 1.2;
    const baseWidth = 1.5;
    const yOffset = baseHeight / 2;  // Correct Y positioning

    // Debug log for static podium rendering
    if (DEBUG) console.log('Rendering static podium at position:', position);

    return (
        <group position={[position[0], 0, position[2]]}>
            <Box
                args={[baseWidth, baseHeight, baseWidth]}
                position={[0, yOffset, 0]}
            >
                <meshStandardMaterial
                    color="#ffffff"
                    metalness={0.1}
                    roughness={0.3}
                />
            </Box>
        </group>
    );
};

// Helper function to validate and ensure safe numbers for Three.js geometries
const ensureSafeNumber = (value, defaultValue = 0) => {
    const num = Number(value);
    return (typeof num === 'number' && isFinite(num) && !isNaN(num)) ? num : defaultValue;
};

// Helper function to validate and ensure safe vector3 values
const ensureSafeVector3 = (x = 0, y = 0, z = 0) => [
    ensureSafeNumber(x),
    ensureSafeNumber(y),
    ensureSafeNumber(z)
];

// Helper function to safely get color from materials config
const getMaterialColor = (roomConfig, path, defaultColor = '#ffffff') => {
    try {
        if (!roomConfig || !roomConfig.materials) return defaultColor;
        
        const parts = path.split('.');
        let value = roomConfig.materials;
        for (const part of parts) {
            if (!value || !value[part]) return defaultColor;
            value = value[part];
        }
        return value?.color || defaultColor;
    } catch (error) {
        console.warn(`Failed to get material color for path ${path}:`, error);
        return defaultColor;
    }
};

function Room({
    onSpotSelect,
    assets,
    canInteract,
    isShowMode,
    selectedSpot,
    controlsRef,
    galleryId,
    onAssetsUpdate,
    dimensions: rawDimensions,
    roomType = 'classic',  // Default to classic for backward compatibility
    onHover,
    onRoomLoaded,
    onTexturesLoaded,
    glbPath = null  // Path to GLB file for custom rooms
}) {
    console.log('Room component received roomType:', roomType);
    
    const dimensions = createSafeDimensions(rawDimensions);
    const { width, height, depth, wallThickness } = dimensions;

    // Use the room hook to manage room state and textures
    const {
        roomConfig,
        createWallMaterial,
        createFloorMaterial,
        texturesLoading,
        error
    } = useRoom(roomType);  // Use the roomType prop here

    // Call loading callbacks when states change
    useEffect(() => {
        if (roomConfig && !texturesLoading) {
            onRoomLoaded?.();
        }
    }, [roomConfig, onRoomLoaded]);

    useEffect(() => {
        if (!texturesLoading) {
            onTexturesLoaded?.();
        }
    }, [texturesLoading, onTexturesLoaded]);

    // Get safe dimensions - need to be defined early but use safe defaults if roomConfig is null
    const safeWidth = ensureSafeNumber(roomConfig?.dimensions?.width, 35);
    const safeHeight = ensureSafeNumber(roomConfig?.dimensions?.height, 8);
    const safeDepth = ensureSafeNumber(roomConfig?.dimensions?.depth, 25);
    const safeWallThickness = ensureSafeNumber(roomConfig?.dimensions?.wallThickness, 0.2);
    const safeDoorWidth = ensureSafeNumber(roomConfig?.dimensions?.doorWidth, 4);

    // Initialize all hooks BEFORE any conditional returns
    // Store initial spot positions as defaults
    const [spotDefaults] = useState(() => {
        try {
            if (!roomConfig) return {};
            
            const spots = calculateAllSpotPositions(roomConfig);
            return spots.reduce((acc, spot) => {
                acc[spot.id] = {
                    position: Array.isArray(spot.position) ? spot.position.map(Number) : [0, 0, 0],
                    rotation: Array.isArray(spot.rotation) ? spot.rotation.map(Number) : [0, 0, 0],
                    scale: [1, 1, 1]
                };
                return acc;
            }, {});
        } catch (error) {
            console.error('Failed to calculate spot positions:', error);
            return {};
        }
    });

    // Get the gallery assets hook functionality - before any conditional returns
    const { updateAssetTransform, localAssets } = useGalleryAssets(galleryId, assets, onAssetsUpdate);

    // Keep track of pending transforms - before any conditional returns
    const [pendingTransforms, setPendingTransforms] = useState({});

    // Calculate all display spot positions - before any conditional returns
    const displaySpots = useMemo(() => {
        try {
            if (!roomConfig) return [];
            return calculateAllSpotPositions(roomConfig);
        } catch (error) {
            console.error('Failed to calculate display spots:', error);
            return [];
        }
    }, [roomConfig]);

    // Calculate positions for canvas buttons - before any conditional returns
    const canvasButtons = useMemo(() => [
        // Front wall - center
        {
            wallId: 'front_center',
            position: [0, safeHeight/2, -(safeDepth/2 - safeWallThickness/2 - 0.05)],
            rotation: [0, 0, 0]
        },
        // Front wall - left section
        {
            wallId: 'front_left',
            position: [-safeWidth/3, safeHeight/2, -(safeDepth/2 - safeWallThickness/2 - 0.05)],
            rotation: [0, 0, 0]
        },
        // Front wall - right section
        {
            wallId: 'front_right',
            position: [safeWidth/3, safeHeight/2, -(safeDepth/2 - safeWallThickness/2 - 0.05)],
            rotation: [0, 0, 0]
        },
        // Back wall - center
        {
            wallId: 'back_center',
            position: [0, safeHeight/2, safeDepth/2 - safeWallThickness/2 - 0.05],
            rotation: [0, Math.PI, 0]
        },
        // Back wall - left section
        {
            wallId: 'back_left',
            position: [-safeWidth/3, safeHeight/2, safeDepth/2 - safeWallThickness/2 - 0.05],
            rotation: [0, Math.PI, 0]
        },
        // Back wall - right section
        {
            wallId: 'back_right',
            position: [safeWidth/3, safeHeight/2, safeDepth/2 - safeWallThickness/2 - 0.05],
            rotation: [0, Math.PI, 0]
        },
        // Left wall
        {
            wallId: 'left',
            position: [-(safeWidth/2 - safeWallThickness/2 - 0.05), safeHeight/2, 0],
            rotation: [0, Math.PI/2, 0]
        },
        // Right wall
        {
            wallId: 'right',
            position: [safeWidth/2 - safeWallThickness/2 - 0.05, safeHeight/2, 0],
            rotation: [0, -Math.PI/2, 0]
        },
        // Inner left wall - front section (facing right) - FLIPPED 180°
        {
            wallId: 'inner_left_front',
            position: [-safeWidth/6 + safeWallThickness/2 + 0.05, safeHeight/2, safeDoorWidth/2 + (safeDepth - safeDoorWidth)/4],
            rotation: [0, -Math.PI/2 + Math.PI, 0]
        },
        // Inner left wall - front section (facing left) - FLIPPED 180°
        {
            wallId: 'inner_left_front_opposite',
            position: [-safeWidth/6 - safeWallThickness/2 - 0.05, safeHeight/2, safeDoorWidth/2 + (safeDepth - safeDoorWidth)/4],
            rotation: [0, Math.PI/2 + Math.PI, 0]
        },
        // Inner left wall - back section (facing right) - FLIPPED 180°
        {
            wallId: 'inner_left_back',
            position: [-safeWidth/6 + safeWallThickness/2 + 0.05, safeHeight/2, -safeDoorWidth/2 - (safeDepth - safeDoorWidth)/4],
            rotation: [0, -Math.PI/2 + Math.PI, 0]
        },
        // Inner left wall - back section (facing left) - FLIPPED 180°
        {
            wallId: 'inner_left_back_opposite',
            position: [-safeWidth/6 - safeWallThickness/2 - 0.05, safeHeight/2, -safeDoorWidth/2 - (safeDepth - safeDoorWidth)/4],
            rotation: [0, Math.PI/2 + Math.PI, 0]
        },
        // Inner right wall - front section (facing left) - FLIPPED 180°
        {
            wallId: 'inner_right_front',
            position: [safeWidth/6 - safeWallThickness/2 - 0.05, safeHeight/2, safeDoorWidth/2 + (safeDepth - safeDoorWidth)/4],
            rotation: [0, Math.PI/2 + Math.PI, 0]
        },
        // Inner right wall - front section (facing right) - FLIPPED 180°
        {
            wallId: 'inner_right_front_opposite',
            position: [safeWidth/6 + safeWallThickness/2 + 0.05, safeHeight/2, safeDoorWidth/2 + (safeDepth - safeDoorWidth)/4],
            rotation: [0, -Math.PI/2 + Math.PI, 0]
        },
        // Inner right wall - back section (facing left) - FLIPPED 180°
        {
            wallId: 'inner_right_back',
            position: [safeWidth/6 - safeWallThickness/2 - 0.05, safeHeight/2, -safeDoorWidth/2 - (safeDepth - safeDoorWidth)/4],
            rotation: [0, Math.PI/2 + Math.PI, 0]
        },
        // Inner right wall - back section (facing right) - FLIPPED 180°
        {
            wallId: 'inner_right_back_opposite',
            position: [safeWidth/6 + safeWallThickness/2 + 0.05, safeHeight/2, -safeDoorWidth/2 - (safeDepth - safeDoorWidth)/4],
            rotation: [0, -Math.PI/2 + Math.PI, 0]
        }
    ], [safeWidth, safeHeight, safeDepth, safeWallThickness, safeDoorWidth]);

    // Add debug information about room configuration
    useEffect(() => {
        console.log(`Room hook returned config for ${roomType}:`, roomConfig);
    }, [roomConfig, roomType]);

    // Loading indicator component
    const LoadingIndicator = () => (
        <group position={[0, safeHeight/2, 0]}>
            <mesh>
                <sphereGeometry args={[0.5, 16, 16]} />
                <meshStandardMaterial color="#4285f4" emissive="#4285f4" emissiveIntensity={0.5} />
            </mesh>
            <pointLight intensity={1.0} distance={5} color="#ffffff" />
        </group>
    );

    // Use localAssets instead of props.assets for rendering
    const currentAssets = localAssets || assets;

    // Get safe material colors
    const baseColor = getMaterialColor(roomConfig, 'trim.base', '#cccccc');
    const crownColor = getMaterialColor(roomConfig, 'trim.crown', '#cccccc');
    const pilasterColor = getMaterialColor(roomConfig, 'trim.pilasters', '#cccccc');
    const floorColor = getMaterialColor(roomConfig, 'floor', '#ffffff');
    const ceilingColor = getMaterialColor(roomConfig, 'ceiling', '#ffffff');

    // Helper to ensure array values are valid numbers
    const ensureValidArray = (arr, defaultValue) => {
        if (!Array.isArray(arr)) return defaultValue;
        return arr.map(val => {
            const num = Number(val);
            return isNaN(num) || !isFinite(num) ? 0 : num;
        });
    };

    // Get the effective transform for a spot
    const getEffectiveTransform = (spotId, asset) => {
        // First check pending transforms
        const pending = pendingTransforms[spotId];
        if (pending) {
            return pending;
        }

        // Then check asset transforms
        if (asset?.position || asset?.rotation || asset?.scale) {
            return {
                position: ensureValidArray(asset.position, spotDefaults[spotId]?.position || [0, 0, 0]),
                rotation: ensureValidArray(asset.rotation, spotDefaults[spotId]?.rotation || [0, 0, 0]),
                scale: ensureValidArray(asset.scale, [1, 1, 1])
            };
        }

        // Finally use spot defaults
        return spotDefaults[spotId] || {
            position: [0, 0, 0],
            rotation: [0, 0, 0],
            scale: [1, 1, 1]
        };
    };

    // Update the transform handler
    const handleTransformEnd = async (spotId, transform) => {
        // Get existing asset data
        const existingAsset = currentAssets[spotId];
        if (!existingAsset) return;

        // Create new asset with transform data
        const updatedAsset = {
            ...existingAsset,
            position: ensureValidArray(transform.position, existingAsset.position),
            rotation: ensureValidArray(transform.rotation, existingAsset.rotation),
            scale: ensureValidArray(transform.scale, existingAsset.scale)
        };

        // Store the pending transform
        setPendingTransforms(prev => ({
            ...prev,
            [spotId]: {
                position: updatedAsset.position,
                rotation: updatedAsset.rotation,
                scale: updatedAsset.scale
            }
        }));

        // Update asset with new transform
        await updateAssetTransform(spotId, updatedAsset);
    };

    const calculateSpotPositionForWall = (wallId) => {
        switch(wallId) {
            case 'front':
                return {
                    position: [0, safeHeight/2, -(safeDepth/2 - safeWallThickness - 0.2)],
                    rotation: [0, 0, 0],
                    surfaceNormal: [0, 0, 1]  // Points toward +Z
                };
            case 'back':
                return {
                    position: [0, safeHeight/2, safeDepth/2 - safeWallThickness - 0.2],
                    rotation: [0, Math.PI, 0],
                    surfaceNormal: [0, 0, -1]  // Points toward -Z
                };
            case 'left':
                return {
                    position: [-(safeWidth/2 - safeWallThickness - 0.2), safeHeight/2, 0],
                    rotation: [0, Math.PI/2, 0],
                    surfaceNormal: [1, 0, 0]  // Points toward +X
                };
            case 'right':
                return {
                    position: [safeWidth/2 - safeWallThickness - 0.2, safeHeight/2, 0],
                    rotation: [0, -Math.PI/2, 0],
                    surfaceNormal: [-1, 0, 0]  // Points toward -X
                };
            default:
                return {
                    position: [0, safeHeight/2, 0],
                    rotation: [0, 0, 0],
                    surfaceNormal: [0, 0, 1]
                };
        }
    };

    const handleAddSpot = (spotConfig) => {
        // Generate unique ID for new spot
        const newSpotId = `spot-${spotConfig.wallId}-${Date.now()}`;
        
        // Create empty asset for the spot using the provided position and rotation
        const emptyAsset = {
            type: 'image',
            url: '',
            position: spotConfig.position,
            rotation: spotConfig.rotation,
            scale: [1, 1, 1],
            surfaceNormal: spotConfig.surfaceNormal,
            width: 2,
            height: 2,
            depth: 0.1
        };
        
        // Update gallery assets with the new spot
        if (onAssetsUpdate) {
            const updatedAssets = {
                ...currentAssets,
                [newSpotId]: emptyAsset
            };
            onAssetsUpdate(updatedAssets);
        }
        
        // Also update local state
        updateAssetTransform(newSpotId, emptyAsset);
        
        // Automatically select the newly created canvas
        if (onSpotSelect && !isShowMode) {
            // Use setTimeout to ensure the spot is created before selection
            setTimeout(() => {
                onSpotSelect(newSpotId, true);
            }, 50);
        }
    };

    // State for GLB canvas buttons
    const [glbCanvasButtons, setGlbCanvasButtons] = useState([]);
    
    // Handle canvas buttons detected from GLB file
    const handleCanvasButtonsDetected = useCallback((buttons) => {
        console.log('Detected canvas buttons from GLB:', buttons);
        setGlbCanvasButtons(buttons);
    }, []);
    
    // Check if this is a GLB room type
    if (roomType === ROOM_TYPES.CUSTOM_GLB && glbPath) {
        console.log('Rendering custom GLB room:', glbPath);
        return (
            <group>
                <GlbRoom
                    glbPath={glbPath}
                    onCanvasButtonsDetected={handleCanvasButtonsDetected}
                    onLoad={() => console.log('GLB Room loaded successfully')}
                    scale={1}
                />
                
                {/* Display Spots for assets */}
                {Object.entries(currentAssets || {}).map(([id, asset]) => {
                    const transform = getEffectiveTransform(id, asset);
                    
                    if (asset?.type === 'model' || (asset?.type === 'nft' && asset?.isModel)) {
                        return (
                            <Model3D
                                key={id}
                                id={id}
                                position={ensureSafeVector3(...transform.position)}
                                rotation={ensureSafeVector3(...transform.rotation)}
                                scale={ensureSafeVector3(...transform.scale)}
                                asset={asset}
                                selected={selectedSpot === id}
                                canInteract={!isShowMode}
                                onSelect={onSpotSelect}
                                controlsRef={controlsRef}
                                onTransformEnd={(transform) => handleTransformEnd(id, transform)}
                                isShowMode={isShowMode}
                                onHover={onHover}
                            />
                        );
                    } else {
                        return (
                            <DisplaySpot
                                key={id}
                                id={id}
                                position={ensureSafeVector3(...transform.position)}
                                rotation={ensureSafeVector3(...transform.rotation)}
                                scale={ensureSafeVector3(...transform.scale)}
                                surfaceNormal={asset?.surfaceNormal}
                                asset={asset || { type: 'image' }}
                                isSelected={selectedSpot === id}
                                canInteract={!isShowMode}
                                onSelect={onSpotSelect}
                                controlsRef={controlsRef}
                                onTransformEnd={(transform) => handleTransformEnd(id, transform)}
                                isShowMode={isShowMode}
                                onHover={onHover}
                            />
                        );
                    }
                })}
                
                {/* Canvas Buttons from GLB */}
                {glbCanvasButtons.map((button, index) => (
                    <CanvasButton
                        key={`glb-canvas-button-${index}`}
                        position={button.position}
                        rotation={button.rotation}
                        wallId={button.wallId || 'glb-wall'}
                        onAddSpot={handleAddSpot}
                        isShowMode={isShowMode}
                    />
                ))}
            </group>
        );
    }
    
    // Check if this is a custom GLB room type
    if (roomType === ROOM_TYPES.CUSTOM_GLB || roomType === 'custom-glb') {
        console.log('Rendering custom GLB room with path:', glbPath);
        if (!glbPath) {
            console.error('No GLB path provided for custom GLB room');
            // Fallback to classic room if no GLB path
            return (
                <group>
                    <Box
                        args={[35, 0.2, 25]}
                        position={[0, -0.1, 0]}
                    >
                        <meshStandardMaterial 
                            color="#e8d0b8" 
                            roughness={0.6}
                            metalness={0.1}
                        />
                    </Box>
                    <mesh position={[0, 2, 0]}>
                        <sphereGeometry args={[1, 32, 32]} />
                        <meshStandardMaterial color="red" />
                    </mesh>
                </group>
            );
        }
        
        return (
            <group>
                {/* Render the GLB room */}
                <GlbRoom 
                    glbPath={glbPath}
                    onCanvasButtonsDetected={handleCanvasButtonsDetected}
                    scale={1}
                />
                
                {/* Render spots on the GLB room */}
                {displaySpots.map(spot => {
                    const assetData = currentAssets[spot.id];
                    const transform = getEffectiveTransform(spot.id, assetData);

                    return (
                        <DisplaySpot
                            key={spot.id}
                            id={spot.id}
                            position={ensureSafeVector3(...transform.position)}
                            rotation={ensureSafeVector3(...transform.rotation)}
                            scale={ensureSafeVector3(...transform.scale)}
                            surfaceNormal={assetData?.surfaceNormal}
                            asset={assetData || { type: 'image' }}
                            isSelected={selectedSpot === spot.id}
                            canInteract={!isShowMode}
                            onSelect={onSpotSelect}
                            controlsRef={controlsRef}
                            onTransformEnd={(transform) => handleTransformEnd(spot.id, transform)}
                            isShowMode={isShowMode}
                            onHover={onHover}
                        />
                    );
                })}
                
                {/* Render spots from current assets that aren't in displaySpots */}
                {Object.entries(currentAssets).map(([id, asset]) => {
                    // Skip if this spot is already rendered
                    if (displaySpots.some(spot => spot.id === id)) return null;

                    const transform = getEffectiveTransform(id, asset);

                    return (
                        <DisplaySpot
                            key={id}
                            id={id}
                            position={ensureSafeVector3(...transform.position)}
                            rotation={ensureSafeVector3(...transform.rotation)}
                            scale={ensureSafeVector3(...transform.scale)}
                            surfaceNormal={asset?.surfaceNormal}
                            asset={asset || { type: 'image' }}
                            isSelected={selectedSpot === id}
                            canInteract={!isShowMode}
                            onSelect={onSpotSelect}
                            controlsRef={controlsRef}
                            onTransformEnd={(transform) => handleTransformEnd(id, transform)}
                            isShowMode={isShowMode}
                            onHover={onHover}
                        />
                    );
                })}
                
                {/* 3D Models */}
                {models.map(([id, asset]) => {
                    const transform = getEffectiveTransform(id, asset);
                    
                    return (
                        <Model3D
                            key={id}
                            id={id}
                            position={ensureSafeVector3(...transform.position)}
                            rotation={ensureSafeVector3(...transform.rotation)}
                            scale={ensureSafeVector3(...transform.scale)}
                            asset={asset}
                            selected={selectedSpot === id}
                            canInteract={!isShowMode}
                            onSelect={onSpotSelect}
                            controlsRef={controlsRef}
                            onTransformEnd={(transform) => handleTransformEnd(id, transform)}
                            isShowMode={isShowMode}
                            onHover={onHover}
                        />
                    );
                })}
                
                {/* Canvas Buttons from GLB */}
                {glbCanvasButtons.map((button, index) => (
                    <CanvasButton
                        key={`glb-canvas-button-${index}`}
                        position={button.position}
                        rotation={button.rotation}
                        wallId={button.wallId || 'glb-wall'}
                        onAddSpot={handleAddSpot}
                        isShowMode={isShowMode}
                    />
                ))}
            </group>
        );
    }
    
    // Check if this is a hexagonal room type
    if (roomType === ROOM_TYPES.HEXAGONAL) {
        console.log('Rendering hexagonal room with hallway using createHexagonalRoomGeometry');
        const hexRoom = createHexagonalRoomGeometry({
            roomConfig,
            createWallMaterial,
            onSpotSelect,
            canInteract,
            isShowMode,
            assets: currentAssets,
            selectedSpot,
            controlsRef,
            onAssetsUpdate,
            updateAssetTransform,
            galleryId,
            onHover
        });
        return (
            <group>
                {hexRoom.floor}
                {hexRoom.ceiling}
                {hexRoom.walls}
                {hexRoom.buttons}
                {hexRoom.wallArt}
                {hexRoom.models}
            </group>
        );
    }
    
    // Separate 3D models from wall art using currentAssets
    const models = Object.entries(currentAssets || {}).filter(([id, asset]) => 
        asset?.type === 'model' || (asset?.type === 'nft' && asset?.isModel)
    );

    // Include all 2D images and NFTs that aren't models
    const wallArt = Object.entries(currentAssets || {}).filter(([id, asset]) => 
        asset && !asset.isModel && (asset.type === 'image' || asset.type === 'nft')
    );

    // Debug log to check what's being filtered
    console.log('Asset filtering:', {
        totalAssets: Object.keys(currentAssets || {}).length,
        models: models.length,
        wallArt: wallArt.length,
        wallArtDetails: wallArt.map(([id, asset]) => ({
            id,
            type: asset.type,
            isModel: asset.isModel
        }))
    });

    // Early return if textures are still loading
    if (texturesLoading) {
        console.log('Room showing loading state');
        return (
            <group>
                {/* Simplified loading room to reduce lag */}
                <Box
                    args={[35, 0.2, 25]}
                    position={[0, -0.1, 0]}
                >
                    <meshStandardMaterial 
                        color="#ffffff" 
                        roughness={0.6}
                        metalness={0.1}
                    />
                </Box>

                {/* Loading indicator */}
                <LoadingIndicator />
            </group>
        );
    }
    
    // Early return if room is not ready or has error
    if (!roomConfig || error) {
        console.error('Room initialization error:', error);
        // Show a fallback simple room instead of returning null
        return (
            <group>
                {/* Fallback Floor */}
                <Box
                    args={[35, 0.2, 25]}
                    position={[0, -0.1, 0]}
                >
                    <meshStandardMaterial 
                        color="#ffffff" 
                        roughness={0.2}
                        metalness={0.1}
                    />
                </Box>

                {/* Fallback Walls */}
                <Box 
                    args={[35, 8, 0.2]}
                    position={[0, 4, -12.5]}
                >
                    <meshStandardMaterial 
                        color="#f8f4f0"
                        roughness={0.85}
                        metalness={0}
                    />
                </Box>
                <Box 
                    args={[35, 8, 0.2]}
                    position={[0, 4, 12.5]}
                >
                    <meshStandardMaterial 
                        color="#f8f4f0"
                        roughness={0.85}
                        metalness={0}
                    />
                </Box>
                <Box 
                    args={[0.2, 8, 25]}
                    position={[-17.5, 4, 0]}
                >
                    <meshStandardMaterial 
                        color="#f8f4f0"
                        roughness={0.85}
                        metalness={0}
                    />
                </Box>
                <Box 
                    args={[0.2, 8, 25]}
                    position={[17.5, 4, 0]}
                >
                    <meshStandardMaterial 
                        color="#f8f4f0"
                        roughness={0.85}
                        metalness={0}
                    />
                </Box>

                {/* Fallback Ceiling */}
                <Box 
                    args={[35, 0.2, 25]}
                    position={[0, 8, 0]}
                >
                    <meshStandardMaterial
                        color="#ffffff"
                        roughness={0.7}
                        metalness={0}
                    />
                </Box>
                
                {/* Error message */}
                <group position={[0, 4, 0]}>
                    <mesh>
                        <sphereGeometry args={[0.5, 32, 32]} />
                        <meshStandardMaterial color="red" />
                    </mesh>
                </group>
            </group>
        );
    }

    // Unified rendering for all room types
    if (roomType === 'hexagonal') {
        const hexRoom = createHexagonalRoomGeometry({
            roomConfig,
            createWallMaterial,
            onSpotSelect,
            canInteract,
            isShowMode,
            assets,
            selectedSpot,
            controlsRef,
            onAssetsUpdate,
            updateAssetTransform,
            galleryId,
            onHover
        });
        return (
            <group>
                {hexRoom.floor}
                {hexRoom.ceiling}
                {hexRoom.walls}
                {hexRoom.buttons}
                {hexRoom.wallArt}
                {hexRoom.models}
            </group>
        );
    }

    return (
        <group>
            {/* Floor */}
            <mesh 
                position={[0, 0, 0]} 
                rotation={[-Math.PI/2, 0, 0]}
                onClick={(e) => {
                    e.stopPropagation();
                    if (!isShowMode) onSpotSelect(null);
                }}
            >
                <planeGeometry args={[safeWidth * 1.5, safeDepth * 1.5]} />
                <primitive object={createFloorMaterial(roomConfig?.materials?.floor)} />
            </mesh>
            
            {/* Clear reflective layer - room reflections only */}
            <mesh 
                position={[0, 0.005, 0]} 
                rotation={[-Math.PI/2, 0, 0]}
                renderOrder={-1}
                visible={true}
            >
                <planeGeometry args={[safeWidth * 1.5, safeDepth * 1.5]} />
                <MeshReflectorMaterial
                    blur={[400, 100]}
                    resolution={256}
                    mixBlur={0}
                    mixStrength={0.6}
                    roughness={0.1}
                    depthScale={0}
                    minDepthThreshold={0.9}
                    maxDepthThreshold={1}
                    color="#000000"
                    metalness={0}
                    mirror={0.5}
                    transparent={true}
                    opacity={0.15}
                    depthWrite={false}
                />
            </mesh>
            
            {/* Base Trim */}
            {/* Back base */}
            <Box
                args={ensureSafeVector3(safeWidth, 0.4, 0.15)}
                position={ensureSafeVector3(0, 0.2, -safeDepth / 2 + 0.075)}
                onClick={(e) => {
                    e.stopPropagation();
                    if (!isShowMode) onSpotSelect(null);
                }}
            >
                <meshStandardMaterial 
                    color={baseColor}
                    roughness={roomConfig?.materials?.trim?.baseboards?.roughness || 0.5}
                    metalness={roomConfig?.materials?.trim?.baseboards?.metalness || 0.1}
                />
            </Box>
            {/* Front base */}
            <Box
                args={ensureSafeVector3(safeWidth, 0.4, 0.15)}
                position={ensureSafeVector3(0, 0.2, safeDepth / 2 - 0.075)}
                onClick={(e) => {
                    e.stopPropagation();
                    if (!isShowMode) onSpotSelect(null);
                }}
            >
                <meshStandardMaterial 
                    color={baseColor}
                    roughness={roomConfig?.materials?.trim?.baseboards?.roughness || 0.5}
                    metalness={roomConfig?.materials?.trim?.baseboards?.metalness || 0.1}
                />
            </Box>
            {/* Left base */}
            <Box
                args={ensureSafeVector3(0.15, 0.4, safeDepth)}
                position={ensureSafeVector3(-safeWidth / 2 + 0.075, 0.2, 0)}
                onClick={(e) => {
                    e.stopPropagation();
                    if (!isShowMode) onSpotSelect(null);
                }}
            >
                <meshStandardMaterial 
                    color={baseColor}
                    roughness={roomConfig?.materials?.trim?.baseboards?.roughness || 0.5}
                    metalness={roomConfig?.materials?.trim?.baseboards?.metalness || 0.1}
                />
            </Box>
            {/* Right base */}
            <Box
                args={ensureSafeVector3(0.15, 0.4, safeDepth)}
                position={ensureSafeVector3(safeWidth / 2 - 0.075, 0.2, 0)}
                onClick={(e) => {
                    e.stopPropagation();
                    if (!isShowMode) onSpotSelect(null);
                }}
            >
                <meshStandardMaterial 
                    color={baseColor}
                    roughness={roomConfig?.materials?.trim?.baseboards?.roughness || 0.5}
                    metalness={roomConfig?.materials?.trim?.baseboards?.metalness || 0.1}
                />
            </Box>

            {/* Crown Molding */}
            {/* Back wall crown */}
            <Box 
                args={ensureSafeVector3(safeWidth, 0.3, 0.3)}
                position={ensureSafeVector3(0, safeHeight - 0.15, -safeDepth / 2 + 0.15)}
                onClick={(e) => {
                    e.stopPropagation();
                    if (!isShowMode) onSpotSelect(null);
                }}
            >
                <meshStandardMaterial 
                    color={crownColor}
                    roughness={roomConfig?.materials?.trim?.crown?.roughness || 0.4}
                    metalness={roomConfig?.materials?.trim?.crown?.metalness || 0.2}
                />
            </Box>
            {/* Front wall crown */}
            <Box 
                args={ensureSafeVector3(safeWidth, 0.3, 0.3)}
                position={ensureSafeVector3(0, safeHeight - 0.15, safeDepth / 2 - 0.15)}
                onClick={(e) => {
                    e.stopPropagation();
                    if (!isShowMode) onSpotSelect(null);
                }}
            >
                <primitive object={createWallMaterial(roomConfig?.materials?.walls)} color={crownColor} />
            </Box>
            {/* Left wall crown */}
            <Box 
                args={ensureSafeVector3(0.3, 0.3, safeDepth)}
                position={ensureSafeVector3(-safeWidth / 2 + 0.15, safeHeight - 0.15, 0)}
                onClick={(e) => {
                    e.stopPropagation();
                    if (!isShowMode) onSpotSelect(null);
                }}
            >
                <primitive object={createWallMaterial(roomConfig?.materials?.walls)} color={crownColor} />
            </Box>
            {/* Right wall crown */}
            <Box 
                args={ensureSafeVector3(0.3, 0.3, safeDepth)}
                position={ensureSafeVector3(safeWidth / 2 - 0.15, safeHeight - 0.15, 0)}
                onClick={(e) => {
                    e.stopPropagation();
                    if (!isShowMode) onSpotSelect(null);
                }}
            >
                <primitive object={createWallMaterial(roomConfig?.materials?.walls)} color={crownColor} />
            </Box>

            {/* Pilasters */}
            {/* Left divider pilasters */}
            <Box 
                args={ensureSafeVector3(0.4, safeHeight, 0.4)}
                position={ensureSafeVector3(-safeWidth / 6, safeHeight / 2, -safeDoorWidth / 2)}
                onClick={(e) => {
                    e.stopPropagation();
                    if (!isShowMode) onSpotSelect(null);
                }}
            >
                <primitive object={createWallMaterial(roomConfig?.materials?.walls)} color={pilasterColor} />
            </Box>
            <Box 
                args={ensureSafeVector3(0.4, safeHeight, 0.4)}
                position={ensureSafeVector3(-safeWidth / 6, safeHeight / 2, safeDoorWidth / 2)}
                onClick={(e) => {
                    e.stopPropagation();
                    if (!isShowMode) onSpotSelect(null);
                }}
            >
                <primitive object={createWallMaterial(roomConfig?.materials?.walls)} color={pilasterColor} />
            </Box>
            {/* Right divider pilasters */}
            <Box 
                args={ensureSafeVector3(0.4, safeHeight, 0.4)}
                position={ensureSafeVector3(safeWidth / 6, safeHeight / 2, -safeDoorWidth / 2)}
                onClick={(e) => {
                    e.stopPropagation();
                    if (!isShowMode) onSpotSelect(null);
                }}
            >
                <primitive object={createWallMaterial(roomConfig?.materials?.walls)} color={pilasterColor} />
            </Box>
            <Box 
                args={ensureSafeVector3(0.4, safeHeight, 0.4)}
                position={ensureSafeVector3(safeWidth / 6, safeHeight / 2, safeDoorWidth / 2)}
                onClick={(e) => {
                    e.stopPropagation();
                    if (!isShowMode) onSpotSelect(null);
                }}
            >
                <primitive object={createWallMaterial(roomConfig?.materials?.walls)} color={pilasterColor} />
            </Box>

            {/* Main Walls */}
            {/* Back wall */}
            <Box 
                args={ensureSafeVector3(safeWidth, safeHeight, safeWallThickness)}
                position={ensureSafeVector3(0, safeHeight / 2, -safeDepth / 2)}
                onClick={(e) => {
                    e.stopPropagation();
                    if (!isShowMode) onSpotSelect(null);
                }}
            >
                <primitive object={createWallMaterial(roomConfig?.materials?.walls)} />
            </Box>
            {/* Front wall */}
            <Box 
                args={ensureSafeVector3(safeWidth, safeHeight, safeWallThickness)}
                position={ensureSafeVector3(0, safeHeight / 2, safeDepth / 2)}
                onClick={(e) => {
                    e.stopPropagation();
                    if (!isShowMode) onSpotSelect(null);
                }}
            >
                <primitive object={createWallMaterial(roomConfig?.materials?.walls)} />
            </Box>
            {/* Left wall */}
            <Box 
                args={ensureSafeVector3(safeWallThickness, safeHeight, safeDepth)}
                position={ensureSafeVector3(-safeWidth / 2, safeHeight / 2, 0)}
                onClick={(e) => {
                    e.stopPropagation();
                    if (!isShowMode) onSpotSelect(null);
                }}
            >
                <primitive object={createWallMaterial(roomConfig?.materials?.walls)} />
            </Box>
            {/* Right wall */}
            <Box 
                args={ensureSafeVector3(safeWallThickness, safeHeight, safeDepth)}
                position={ensureSafeVector3(safeWidth / 2, safeHeight / 2, 0)}
                onClick={(e) => {
                    e.stopPropagation();
                    if (!isShowMode) onSpotSelect(null);
                }}
            >
                <primitive object={createWallMaterial(roomConfig?.materials?.walls)} />
            </Box>

            {/* Interior Walls */}
            <Box 
                args={ensureSafeVector3(safeWallThickness, safeHeight, (safeDepth - safeDoorWidth) / 2)}
                position={ensureSafeVector3(-safeWidth / 6, safeHeight / 2, -safeDoorWidth / 2 - (safeDepth - safeDoorWidth) / 4)}
                onClick={(e) => {
                    e.stopPropagation();
                    if (!isShowMode) onSpotSelect(null);
                }}
            >
                <primitive object={createWallMaterial(roomConfig?.materials?.walls)} />
            </Box>
            <Box 
                args={ensureSafeVector3(safeWallThickness, safeHeight, (safeDepth - safeDoorWidth) / 2)}
                position={ensureSafeVector3(-safeWidth / 6, safeHeight / 2, safeDoorWidth / 2 + (safeDepth - safeDoorWidth) / 4)}
                onClick={(e) => {
                    e.stopPropagation();
                    if (!isShowMode) onSpotSelect(null);
                }}
            >
                <primitive object={createWallMaterial(roomConfig?.materials?.walls)} />
            </Box>
            <Box 
                args={ensureSafeVector3(safeWallThickness, safeHeight, (safeDepth - safeDoorWidth) / 2)}
                position={ensureSafeVector3(safeWidth / 6, safeHeight / 2, -safeDoorWidth / 2 - (safeDepth - safeDoorWidth) / 4)}
                onClick={(e) => {
                    e.stopPropagation();
                    if (!isShowMode) onSpotSelect(null);
                }}
            >
                <primitive object={createWallMaterial(roomConfig?.materials?.walls)} />
            </Box>
            <Box 
                args={ensureSafeVector3(safeWallThickness, safeHeight, (safeDepth - safeDoorWidth) / 2)}
                position={ensureSafeVector3(safeWidth / 6, safeHeight / 2, safeDoorWidth / 2 + (safeDepth - safeDoorWidth) / 4)}
                onClick={(e) => {
                    e.stopPropagation();
                    if (!isShowMode) onSpotSelect(null);
                }}
            >
                <primitive object={createWallMaterial(roomConfig?.materials?.walls)} />
            </Box>

            {/* Ceiling */}
            <Box 
                args={ensureSafeVector3(safeWidth, safeWallThickness, safeDepth)}
                position={ensureSafeVector3(0, safeHeight, 0)}
                onClick={(e) => {
                    e.stopPropagation();
                    if (!isShowMode) onSpotSelect(null);
                }}
            >
                <meshStandardMaterial
                    color={ceilingColor}
                    roughness={roomConfig?.materials?.ceiling?.roughness || 0.7}
                    metalness={roomConfig?.materials?.ceiling?.metalness || 0}
                    envMapIntensity={roomConfig?.materials?.ceiling?.envMapIntensity || 0.8}
                />
            </Box>

            {/* Display Spots */}
            {displaySpots.map(spot => {
                const assetData = currentAssets[spot.id];
                const transform = getEffectiveTransform(spot.id, assetData);

                return (
                    <DisplaySpot
                        key={spot.id}
                        id={spot.id}
                        position={ensureSafeVector3(...transform.position)}
                        rotation={ensureSafeVector3(...transform.rotation)}
                        scale={ensureSafeVector3(...transform.scale)}
                        surfaceNormal={assetData?.surfaceNormal}
                        asset={assetData || { type: 'image' }}  // Provide default empty asset
                        isSelected={selectedSpot === spot.id}
                        canInteract={!isShowMode}
                        onSelect={onSpotSelect}
                        controlsRef={controlsRef}
                        onTransformEnd={(transform) => handleTransformEnd(spot.id, transform)}
                        isShowMode={isShowMode}
                        onHover={onHover}
                    />
                );
            })}

            {/* Render spots from current assets that aren't in displaySpots */}
            {Object.entries(currentAssets).map(([id, asset]) => {
                // Skip if this spot is already rendered
                if (displaySpots.some(spot => spot.id === id)) return null;

                const transform = getEffectiveTransform(id, asset);

                return (
                    <DisplaySpot
                        key={id}
                        id={id}
                        position={ensureSafeVector3(...transform.position)}
                        rotation={ensureSafeVector3(...transform.rotation)}
                        scale={ensureSafeVector3(...transform.scale)}
                        surfaceNormal={asset?.surfaceNormal}
                        asset={asset || { type: 'image' }}  // Provide default empty asset
                        isSelected={selectedSpot === id}
                        canInteract={!isShowMode}
                        onSelect={onSpotSelect}
                        controlsRef={controlsRef}
                        onTransformEnd={(transform) => handleTransformEnd(id, transform)}
                        isShowMode={isShowMode}
                        onHover={onHover}
                    />
                );
            })}

            {/* 3D Models */}
            {models.map(([id, asset]) => {
                const transform = getEffectiveTransform(id, asset);
                
                return (
                    <Model3D
                        key={id}
                        id={id}
                        position={ensureSafeVector3(...transform.position)}
                        rotation={ensureSafeVector3(...transform.rotation)}
                        scale={ensureSafeVector3(...transform.scale)}
                        asset={asset}
                        selected={selectedSpot === id}
                        canInteract={!isShowMode}
                        onSelect={onSpotSelect}
                        controlsRef={controlsRef}
                        onTransformEnd={(transform) => handleTransformEnd(id, transform)}
                        isShowMode={isShowMode}
                        onHover={onHover}
                    />
                );
            })}

            {/* Podiums */}
            {roomConfig.podiumPositions.map((pos, index) => (
                <StaticPodium
                    key={`podium-${index}`}
                    position={pos}
                />
            ))}

            {/* Add Canvas Buttons */}
            {canvasButtons.map((button, index) => (
                <CanvasButton
                    key={`canvas-button-${index}`}
                    position={button.position}
                    rotation={button.rotation}
                    wallId={button.wallId}
                    onAddSpot={handleAddSpot}
                    isShowMode={isShowMode}
                />
            ))}
        </group>
    );
}

export default Room; 